import type { FeatureCategory } from '../feature-manifest/types';
import type { Tutorial, TutorialChapter } from './types';
import { TUTORIALS } from './tutorials';

export * from './types';
export * from './validate';
export { TUTORIALS } from './tutorials';

/** Slug de la section « Tutoriels » du centre d'aide (docs-site). */
export const TUTORIALS_DOCS_SLUG = 'tutoriels';

/**
 * Chemin d'un tutoriel sur le centre d'aide (`/tutoriels/<id>`), ou de la page
 * de tous les tutoriels. Partagé par le docs-site (qui rend ces pages) et Pulse
 * (qui y renvoie) : un seul endroit décide de l'URL.
 */
export function tutorialDocsPath(tutorial?: Pick<Tutorial, 'id'>): string {
  return tutorial ? `/${TUTORIALS_DOCS_SLUG}/${tutorial.id}` : `/${TUTORIALS_DOCS_SLUG}`;
}

/** Index `id → Tutorial`. */
export const TUTORIAL_BY_ID: ReadonlyMap<string, Tutorial> = new Map(
  TUTORIALS.map((tutorial): [string, Tutorial] => [tutorial.id, tutorial]),
);

/**
 * Clé de section d'un tutoriel : la catégorie, ou `catégorie/sous-section`
 * quand la catégorie est découpée (ex. `marketing/blog`). C'est l'unité qui
 * décide en tête de quel index la vidéo est remontée.
 */
export function tutorialSectionKey(category: string, subsection?: string): string {
  return subsection ? `${category}/${subsection}` : category;
}

/** Index `section → Tutorial` — la vidéo à remonter en tête d'un index. */
export const TUTORIAL_BY_SECTION: ReadonlyMap<string, Tutorial> = new Map(
  TUTORIALS.filter((tutorial): boolean => tutorial.category !== undefined).map((tutorial): [string, Tutorial] => [
    tutorialSectionKey(tutorial.category as string, tutorial.subsection),
    tutorial,
  ]),
);

/** Le chapitre qui couvre une feature, et le tutoriel auquel il appartient. */
export interface TutorialCoverage {
  tutorial: Tutorial;
  /** Le chapitre dédié à la feature — absent si la couverture vient de la section. */
  chapter?: TutorialChapter;
}

/**
 * Index `feature id → chapitre qui la couvre`. Alimente l'encart remontant des
 * fiches de doc (« expliqué dans le tutoriel X, à 3:20 ») ; `validateTutorials`
 * garantit qu'une feature n'est couverte que par un seul chapitre.
 */
export const TUTORIAL_COVERAGE_BY_FEATURE_ID: ReadonlyMap<string, TutorialCoverage> = ((): ReadonlyMap<
  string,
  TutorialCoverage
> => {
  const map = new Map<string, TutorialCoverage>();
  for (const tutorial of TUTORIALS) {
    for (const chapter of tutorial.chapters) {
      // Premier chapitre qui cite la fiche : c'est là que l'encart l'envoie.
      for (const ref of chapter.feature_refs ?? []) if (!map.has(ref)) map.set(ref, { tutorial, chapter });
    }
  }
  return map;
})();

/**
 * Tutoriel pertinent pour une feature : son chapitre dédié s'il existe, sinon
 * le tutoriel de sa section (repli — d'abord la sous-section, puis la
 * catégorie). `undefined` si rien ne la couvre.
 */
export function findTutorialCoverage(
  featureId: string,
  category?: FeatureCategory,
  subsection?: string,
): TutorialCoverage | undefined {
  const direct = TUTORIAL_COVERAGE_BY_FEATURE_ID.get(featureId);
  if (direct) return direct;
  if (!category) return undefined;
  const fallback =
    (subsection ? TUTORIAL_BY_SECTION.get(tutorialSectionKey(category, subsection)) : undefined) ??
    TUTORIAL_BY_SECTION.get(category);
  return fallback ? { tutorial: fallback } : undefined;
}
